import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ARTWORKS } from "@/data/artworks";
import { ArtworkCard } from "@/components/art/ArtworkCard";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: "easeOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Art() {
  return (
    <div data-testid="art-page" className="bg-[#FBFAF6]">
      {/* HERO */}
      <section className="flex items-center" style={{ minHeight: "min(72vh, 760px)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 w-full pt-28 pb-16 text-center">
          <Reveal>
            <h1
              className="serif tracking-tight text-[#2F2C28] leading-[1.02]"
              style={{ fontSize: "clamp(2.6rem, 6vw, 6rem)" }}
            >
              Art by <span className="italic">Anissa</span>
            </h1>
            <p className="mt-8 text-[16px] sm:text-[18px] leading-[1.8] text-[#2F2C28] max-w-xl mx-auto">
              Original works exploring memory, intimacy and the things we don&rsquo;t always say.
            </p>
            <a
              href="#collection"
              data-testid="view-collection-link"
              className="inline-block mt-12 text-[11px] uppercase tracking-[0.28em] text-[#2F2C28] font-medium border-b border-[#2F2C28]/40 pb-2 hover:border-[#2F2C28] transition-colors"
            >
              View the Collection ↓
            </a>
            <div className="mt-10 flex items-center justify-center gap-8">
              <a href="#collection" data-testid="art-nav-collection" className="text-[11px] uppercase tracking-[0.2em] text-[#55524C] hover:text-[#2F2C28] transition-colors">
                Collection
              </a>
              <Link to="/art/about" data-testid="art-nav-about" className="text-[11px] uppercase tracking-[0.2em] text-[#55524C] hover:text-[#2F2C28] transition-colors">
                About the Artist
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" className="pt-16 pb-28 lg:pb-40 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-20 md:gap-y-28 lg:gap-y-32">
            {ARTWORKS.map((artwork, i) => (
              <Reveal key={artwork.id} delay={0.05 * (i % 2)}>
                <ArtworkCard artwork={artwork} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT THE ARTIST — teaser */}
      <section className="py-28 lg:py-40 bg-[#F2EFE5] border-y border-[#E7E2D6]">
        <div className="max-w-[820px] mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <div className="label-eyebrow">About the Artist</div>
            <p className="serif mt-8 text-[26px] sm:text-[36px] leading-[1.4] text-[#2F2C28]">
              Painting gives form to the things that are sometimes difficult to put into words.
            </p>
            <Link
              to="/art/about"
              data-testid="read-about-anissa-link"
              className="inline-block mt-12 text-[11px] uppercase tracking-[0.28em] text-[#2F2C28] font-medium border-b border-[#2F2C28]/40 pb-2 hover:border-[#2F2C28] transition-colors"
            >
              Read About Anissa →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ENQUIRY CTA */}
      <section className="py-28 lg:py-40">
        <div className="max-w-[820px] mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <div className="label-eyebrow">Acquisitions</div>
            <h2 className="serif mt-6 text-[32px] sm:text-[48px] leading-tight text-[#2F2C28]">
              For viewings, commissions, and considered enquiries.
            </h2>
            <Link to="/contact" className="btn-primary mt-12 inline-flex" data-testid="art-enquire-cta">
              Enquire to Acquire
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
